import { isDefined } from "@clipboard-health/util-ts";
import type { CustomParamsSerializer } from "axios";
import { stringify } from "qs";
import type { z } from "zod";

import { isProductionNodeEnvironment } from "../environment";
import { APP_V2_APP_EVENTS, logEvent } from "../lib/analytics";
import { apiClient, SchemaValidationError } from "./api";

const defaultParamsSerializer: CustomParamsSerializer = (params) =>
  stringify(params, { arrayFormat: "comma" });

/**
 * A stricter Zod schema constraint that uses `unknown` instead of `any` for generic parameters.
 * This eliminates false positives from @typescript-eslint/no-unsafe-* rules while preserving
 * full type inference at call sites via z.input<T> and z.output<T>.
 */
export type ZodSchema = z.ZodType<unknown, z.ZodTypeDef, unknown>;

type InputType = "body" | "queryParams";

interface ParsedApiBaseParams<ResponseSchema extends ZodSchema> {
  url: string;
  responseSchema: ResponseSchema;
  customHeaders?: Record<string, string>;
}

// Query params: if provided, schema is required
type QueryParamsConfig<QueryParamsSchema extends ZodSchema | undefined> =
  QueryParamsSchema extends ZodSchema
    ? { queryParamsSchema: QueryParamsSchema; queryParams: z.input<QueryParamsSchema> }
    : { queryParamsSchema?: undefined; queryParams?: undefined };

// Body: if provided, schema is required
type BodyConfig<BodySchema extends ZodSchema | undefined> = BodySchema extends ZodSchema
  ? { bodySchema: BodySchema; data: z.input<BodySchema> }
  : { bodySchema?: undefined; data?: undefined };

type ParsedGetParams<
  ResponseSchema extends ZodSchema,
  QueryParamsSchema extends ZodSchema | undefined = undefined,
> = ParsedApiBaseParams<ResponseSchema> &
  QueryParamsConfig<QueryParamsSchema> & {
    paramsSerializer?: CustomParamsSerializer;
  };

type ParsedBodyParams<
  ResponseSchema extends ZodSchema,
  BodySchema extends ZodSchema,
  QueryParamsSchema extends ZodSchema | undefined = undefined,
> = ParsedApiBaseParams<ResponseSchema> &
  QueryParamsConfig<QueryParamsSchema> & {
    bodySchema: BodySchema;
    data: z.input<BodySchema>;
    paramsSerializer?: CustomParamsSerializer;
  };

type ParsedDeleteParams<
  ResponseSchema extends ZodSchema,
  BodySchema extends ZodSchema | undefined = undefined,
  QueryParamsSchema extends ZodSchema | undefined = undefined,
> = ParsedApiBaseParams<ResponseSchema> &
  QueryParamsConfig<QueryParamsSchema> &
  BodyConfig<BodySchema> & {
    paramsSerializer?: CustomParamsSerializer;
  };

interface ParsedApiResponse<ResponseSchema extends ZodSchema> {
  data: z.output<ResponseSchema>;
  status: number;
  statusText: string;
}

function logInputValidationError({
  error,
  url,
  method,
  inputType,
}: {
  error: z.ZodError;
  url: string;
  method: string;
  inputType: InputType;
}): void {
  if (isProductionNodeEnvironment()) {
    logEvent(APP_V2_APP_EVENTS.API_INPUT_SCHEMA_VALIDATION_ERROR, {
      errors: error.errors,
      metadata: { url, method, inputType, parseSchema: true },
    });
  }
}

function logResponseValidationError({
  error,
  url,
  method,
}: {
  error: z.ZodError;
  url: string;
  method: string;
}): void {
  if (isProductionNodeEnvironment()) {
    logEvent(APP_V2_APP_EVENTS.API_SCHEMA_VALIDATION_ERROR, {
      errors: error.errors,
      metadata: { url, method, parseSchema: true },
    });
  }
}

async function parseInput<Schema extends ZodSchema>({
  schema,
  data,
  url,
  method,
  inputType,
}: {
  schema: Schema;
  data: z.input<Schema>;
  url: string;
  method: string;
  inputType: InputType;
}): Promise<z.output<Schema>> {
  const result = await schema.safeParseAsync(data);

  if (!result.success) {
    logInputValidationError({ error: result.error, url, method, inputType });
    throw new SchemaValidationError(result.error, { url, method });
  }

  return result.data as z.output<Schema>;
}

async function parseResponse<Schema extends ZodSchema>({
  schema,
  data,
  url,
  method,
}: {
  schema: Schema;
  data: unknown;
  url: string;
  method: string;
}): Promise<z.output<Schema>> {
  const result = await schema.safeParseAsync(data);

  if (!result.success) {
    logResponseValidationError({ error: result.error, url, method });
    throw new SchemaValidationError(result.error, { url, method });
  }

  return result.data as z.output<Schema>;
}

export async function get<
  ResponseSchema extends ZodSchema,
  QueryParamsSchema extends ZodSchema | undefined = undefined,
>({
  url,
  queryParams,
  queryParamsSchema,
  customHeaders,
  responseSchema,
  paramsSerializer,
}: ParsedGetParams<ResponseSchema, QueryParamsSchema>): Promise<ParsedApiResponse<ResponseSchema>> {
  const parsedQueryParams = isDefined(queryParamsSchema)
    ? await parseInput({
        schema: queryParamsSchema,
        data: queryParams,
        url,
        method: "GET",
        inputType: "queryParams",
      })
    : queryParams;

  const response = await apiClient.get<z.output<ResponseSchema>>(url, {
    params: parsedQueryParams,
    headers: customHeaders,
    paramsSerializer: paramsSerializer ?? defaultParamsSerializer,
  });

  const parsedData = await parseResponse({
    schema: responseSchema,
    data: response.data,
    url,
    method: "GET",
  });

  return {
    data: parsedData,
    status: response.status,
    statusText: response.statusText,
  };
}

export async function post<
  ResponseSchema extends ZodSchema,
  BodySchema extends ZodSchema,
  QueryParamsSchema extends ZodSchema | undefined = undefined,
>({
  url,
  data,
  bodySchema,
  queryParams,
  queryParamsSchema,
  customHeaders,
  responseSchema,
  paramsSerializer,
}: ParsedBodyParams<ResponseSchema, BodySchema, QueryParamsSchema>): Promise<
  ParsedApiResponse<ResponseSchema>
> {
  const parsedBody = await parseInput({
    schema: bodySchema,
    data,
    url,
    method: "POST",
    inputType: "body",
  });

  const parsedQueryParams = isDefined(queryParamsSchema)
    ? await parseInput({
        schema: queryParamsSchema,
        data: queryParams,
        url,
        method: "POST",
        inputType: "queryParams",
      })
    : queryParams;

  const response = await apiClient.post<z.output<ResponseSchema>>(url, parsedBody, {
    params: parsedQueryParams,
    headers: customHeaders,
    paramsSerializer: paramsSerializer ?? defaultParamsSerializer,
  });

  const parsedData = await parseResponse({
    schema: responseSchema,
    data: response.data,
    url,
    method: "POST",
  });

  return {
    data: parsedData,
    status: response.status,
    statusText: response.statusText,
  };
}

export async function put<
  ResponseSchema extends ZodSchema,
  BodySchema extends ZodSchema,
  QueryParamsSchema extends ZodSchema | undefined = undefined,
>({
  url,
  data,
  bodySchema,
  queryParams,
  queryParamsSchema,
  customHeaders,
  responseSchema,
  paramsSerializer,
}: ParsedBodyParams<ResponseSchema, BodySchema, QueryParamsSchema>): Promise<
  ParsedApiResponse<ResponseSchema>
> {
  const parsedBody = await parseInput({
    schema: bodySchema,
    data,
    url,
    method: "PUT",
    inputType: "body",
  });

  const parsedQueryParams = isDefined(queryParamsSchema)
    ? await parseInput({
        schema: queryParamsSchema,
        data: queryParams,
        url,
        method: "PUT",
        inputType: "queryParams",
      })
    : queryParams;

  const response = await apiClient.put<z.output<ResponseSchema>>(url, parsedBody, {
    params: parsedQueryParams,
    headers: customHeaders,
    paramsSerializer: paramsSerializer ?? defaultParamsSerializer,
  });

  const parsedData = await parseResponse({
    schema: responseSchema,
    data: response.data,
    url,
    method: "PUT",
  });

  return {
    data: parsedData,
    status: response.status,
    statusText: response.statusText,
  };
}

export async function patch<
  ResponseSchema extends ZodSchema,
  BodySchema extends ZodSchema,
  QueryParamsSchema extends ZodSchema | undefined = undefined,
>({
  url,
  data,
  bodySchema,
  queryParams,
  queryParamsSchema,
  customHeaders,
  responseSchema,
  paramsSerializer,
}: ParsedBodyParams<ResponseSchema, BodySchema, QueryParamsSchema>): Promise<
  ParsedApiResponse<ResponseSchema>
> {
  const parsedBody = await parseInput({
    schema: bodySchema,
    data,
    url,
    method: "PATCH",
    inputType: "body",
  });

  const parsedQueryParams = isDefined(queryParamsSchema)
    ? await parseInput({
        schema: queryParamsSchema,
        data: queryParams,
        url,
        method: "PATCH",
        inputType: "queryParams",
      })
    : queryParams;

  const response = await apiClient.patch<z.output<ResponseSchema>>(url, parsedBody, {
    params: parsedQueryParams,
    headers: customHeaders,
    paramsSerializer: paramsSerializer ?? defaultParamsSerializer,
  });

  const parsedData = await parseResponse({
    schema: responseSchema,
    data: response.data,
    url,
    method: "PATCH",
  });

  return {
    data: parsedData,
    status: response.status,
    statusText: response.statusText,
  };
}

export async function remove<
  ResponseSchema extends ZodSchema,
  BodySchema extends ZodSchema | undefined = undefined,
  QueryParamsSchema extends ZodSchema | undefined = undefined,
>({
  url,
  data,
  bodySchema,
  queryParams,
  queryParamsSchema,
  customHeaders,
  responseSchema,
  paramsSerializer,
}: ParsedDeleteParams<ResponseSchema, BodySchema, QueryParamsSchema>): Promise<
  ParsedApiResponse<ResponseSchema>
> {
  const parsedBody = isDefined(bodySchema)
    ? await parseInput({
        schema: bodySchema,
        data,
        url,
        method: "DELETE",
        inputType: "body",
      })
    : data;

  const parsedQueryParams = isDefined(queryParamsSchema)
    ? await parseInput({
        schema: queryParamsSchema,
        data: queryParams,
        url,
        method: "DELETE",
        inputType: "queryParams",
      })
    : queryParams;

  const response = await apiClient.delete<z.output<ResponseSchema>>(url, {
    data: parsedBody,
    params: parsedQueryParams,
    headers: customHeaders,
    paramsSerializer: paramsSerializer ?? defaultParamsSerializer,
  });

  const parsedData = await parseResponse({
    schema: responseSchema,
    data: response.data,
    url,
    method: "DELETE",
  });

  return {
    data: parsedData,
    status: response.status,
    statusText: response.statusText,
  };
}
